import { useState } from "react";
import { toast } from "sonner";
import { FadeUp } from "./Reveal";
import { useLang } from "./LanguageContext";

const expOptions = {
  en: ["Live Mozzarella & Burrata Station", "Grand Pentolo Show Cooking", "The Full Experience (Mozzarella + Pentolo)", "Venue / Restaurant Consulting"],
  it: ["Stazione Live Mozzarella & Burrata", "Grand Pentolo Show Cooking", "The Full Experience (Mozzarella + Pentolo)", "Consulenza Locali / Ristoranti"],
  ru: ["Станция Live Mozzarella & Burrata", "Grand Pentolo Show Cooking", "The Full Experience (Mozzarella + Pentolo)", "Консалтинг для ресторанов и площадок"],
  es: ["Estación Live Mozzarella & Burrata", "Grand Pentolo Show Cooking", "The Full Experience (Mozzarella + Pentolo)", "Consultoría para Locales / Restaurantes"],
};

const copy = {
  en: {
    kicker: "By Invitation · Private Enquiries",
    h2a: "Request a",
    h2b: "private presentation.",
    body: "Discover how the Live Mozzarella Experience can be composed for your venue.",
    line1: "Private events · Bespoke culinary performance · Full-concept venues",
    line2: "Available for select partners worldwide",
    name: "Full name",
    role: "Role / Company",
    email: "Email address",
    phone: "WhatsApp / Phone",
    experience: "Select experience",
    guests: "Estimated guests (optional)",
    location: "Event location",
    date: "Approximate date",
    details: "Notes — anything you'd like us to know (optional)",
    cta: "Request a Presentation",
    errFields: "Please add your name and email.",
    errEmail: "Please enter a valid email address.",
    ok: "Request received",
    okDesc: (n) => `Thank you, ${n}. We'll be in touch to arrange your private presentation.`,
    err: "Something went wrong. Please try again.",
    wa: "Message us on WhatsApp",
  },
  it: {
    kicker: "Su Invito · Richieste Private",
    h2a: "Richiedi una",
    h2b: "presentazione privata.",
    body: "Scopri come la Live Mozzarella Experience può essere composta per la tua sala.",
    line1: "Eventi privati · Performance culinaria su misura · Locali full-concept",
    line2: "Disponibile per partner selezionati in tutto il mondo",
    name: "Nome e cognome",
    role: "Ruolo / Azienda",
    email: "Indirizzo email",
    phone: "WhatsApp / Telefono",
    experience: "Scegli l'esperienza",
    guests: "Ospiti stimati (facoltativo)",
    location: "Località dell'evento",
    date: "Data indicativa",
    details: "Note — qualcosa che vuoi farci sapere (facoltativo)",
    cta: "Richiedi una Presentazione",
    errFields: "Inserisci nome ed email.",
    errEmail: "Inserisci un indirizzo email valido.",
    ok: "Richiesta ricevuta",
    okDesc: (n) => `Grazie, ${n}. Ti contatteremo per organizzare la tua presentazione privata.`,
    err: "Qualcosa è andato storto. Riprova.",
    wa: "Scrivici su WhatsApp",
  },
  ru: {
    kicker: "По приглашению · Частные запросы",
    h2a: "Запросить",
    h2b: "частную презентацию.",
    body: "Узнайте, как Live Mozzarella Experience может быть создан для вашего заведения.",
    line1: "Частные события · Кейтеринг на заказ · Заведения full-concept",
    line2: "Доступно для избранных партнёров по всему миру",
    name: "Имя и фамилия",
    role: "Роль / Компания",
    email: "Электронная почта",
    phone: "WhatsApp / Телефон",
    experience: "Выберите формат",
    guests: "Гостей, примерно (необяз.)",
    location: "Место события",
    date: "Ориентировочная дата",
    details: "Заметки — что нам стоит знать (необязательно)",
    cta: "Запросить презентацию",
    errFields: "Укажите имя и email.",
    errEmail: "Укажите корректный email.",
    ok: "Запрос получен",
    okDesc: (n) => `Спасибо, ${n}. Мы свяжемся с вами, чтобы организовать частную презентацию.`,
    err: "Что-то пошло не так. Попробуйте ещё раз.",
    wa: "Напишите нам в WhatsApp",
  },
  es: {
    kicker: "Con Invitación · Consultas Privadas",
    h2a: "Solicita una",
    h2b: "presentación privada.",
    body: "Descubre cómo la Live Mozzarella Experience puede componerse para tu salón.",
    line1: "Eventos privados · Performance culinaria a medida · Locales full-concept",
    line2: "Disponible para socios selectos en todo el mundo",
    name: "Nombre y apellido",
    role: "Cargo / Empresa",
    email: "Correo electrónico",
    phone: "WhatsApp / Teléfono",
    experience: "Elige la experiencia",
    guests: "Invitados estimados (opcional)",
    location: "Lugar del evento",
    date: "Fecha aproximada",
    details: "Notas — algo que quieras contarnos (opcional)",
    cta: "Solicitar una Presentación",
    errFields: "Agrega tu nombre y correo.",
    errEmail: "Ingresa un correo electrónico válido.",
    ok: "Solicitud recibida",
    okDesc: (n) => `Gracias, ${n}. Te contactaremos para organizar tu presentación privada.`,
    err: "Algo salió mal. Inténtalo de nuevo.",
    wa: "Escríbenos por WhatsApp",
  },
};

const empty = { name: "", role: "", email: "", phone: "", experience: "", guests: "", location: "", date: "", details: "" };

export const ContactCTA = () => {
  const { lang } = useLang();
  const t = copy[lang];
  const opts = expOptions[lang];
  const [form, setForm] = useState(empty);
  const [hp, setHp] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(t.errFields);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error(t.errEmail);
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          type: form.experience || opts[0],
          experience: form.experience || opts[0],
          role: form.role,
          phone: form.phone,
          guests: form.guests,
          location: form.location,
          event_date: form.date,
          details: form.details,
          website: hp,
        }),
      });
      if (!res.ok) throw new Error("request failed");
      toast.success(t.ok, { description: t.okDesc(form.name.split(" ")[0]) });
      setForm(empty);
    } catch (err) {
      toast.error(t.err);
    }
  };

  const field = "w-full bg-transparent border-b border-bg/25 py-3 font-sans text-bg placeholder:text-bg/40 focus:outline-none focus:border-terracotta transition-colors";
  const label = "font-sans text-xs uppercase tracking-[0.2em] text-bg/40";

  return (
    <section id="book" className="py-24 md:py-40 bg-wine text-bg" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <FadeUp className="lg:col-span-6">
          <p className="font-sans text-xs uppercase tracking-[0.35em] text-bg/50 mb-6">{t.kicker}</p>
          <h2 className="font-serif text-5xl md:text-7xl leading-[0.95]">
            {t.h2a} <br />
            <em className="italic">{t.h2b}</em>
          </h2>
          <p className="font-sans text-base md:text-lg text-bg/60 mt-8 max-w-md leading-relaxed">{t.body}</p>
          <div className="mt-10 space-y-2 font-sans text-sm text-bg/70">
            <p>{t.line1}</p>
            <p>{t.line2}</p>
          </div>
        </FadeUp>

        <FadeUp className="lg:col-span-6" delay={0.15}>
          <form onSubmit={submit} className="space-y-7" data-testid="booking-form">
            <input
              type="text"
              name="website"
              value={hp}
              onChange={(e) => setHp(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
              <input data-testid="input-name" className={field} placeholder={t.name} value={form.name} onChange={set("name")} />
              <input data-testid="input-role" className={field} placeholder={t.role} value={form.role} onChange={set("role")} />
              <input data-testid="input-email" type="email" className={field} placeholder={t.email} value={form.email} onChange={set("email")} />
              <input data-testid="input-phone" type="tel" className={field} placeholder={t.phone} value={form.phone} onChange={set("phone")} />
            </div>
            <div>
              <label className={label}>{t.experience}</label>
              <select
                data-testid="select-experience"
                className={`${field} appearance-none cursor-pointer`}
                value={form.experience || opts[0]}
                onChange={set("experience")}
              >
                {opts.map((x) => (
                  <option key={x} value={x} className="bg-ink text-bg">
                    {x}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-7">
              <input
                data-testid="input-guests"
                type="number"
                min="1"
                inputMode="numeric"
                className={`${field} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                placeholder={t.guests}
                value={form.guests}
                onChange={set("guests")}
              />
              <input data-testid="input-location" className={field} placeholder={t.location} value={form.location} onChange={set("location")} />
              <input data-testid="input-date" className={field} placeholder={t.date} value={form.date} onChange={set("date")} />
            </div>
            <textarea data-testid="input-details" rows={3} className={`${field} resize-none`} placeholder={t.details} value={form.details} onChange={set("details")} />
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <button
                data-testid="submit-booking"
                type="submit"
                className="group relative overflow-hidden rounded-full bg-terracotta px-8 py-4 text-sm font-sans tracking-wide text-white w-full md:w-auto"
              >
                <span className="relative z-10">{t.cta}</span>
                <span className="absolute inset-0 bg-bg scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
                <span className="absolute inset-0 z-10 flex items-center justify-center text-wine opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-150 font-sans tracking-wide">
                  {t.cta}
                </span>
              </button>
              <a
                data-testid="whatsapp-button"
                href="https://wa.me/393315490991"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2.5 rounded-full border border-bg/30 px-8 py-4 text-sm font-sans tracking-wide text-bg hover:border-terracotta hover:text-terracotta transition-colors w-full md:w-auto"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                  <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5.1-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1-.2.2-.6.8-.8 1-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-2-1.2 7.5 7.5 0 0 1-1.4-1.7c-.1-.2 0-.4.1-.5l.4-.5c.1-.2.2-.3.3-.5s0-.4 0-.5l-.8-1.9c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.8 11.9 11.9 0 0 0 4.6 4 15 15 0 0 0 1.5.6 3.6 3.6 0 0 0 1.7.1 2.8 2.8 0 0 0 1.8-1.3 2.2 2.2 0 0 0 .2-1.3c-.1-.1-.2-.2-.5-.3z" />
                </svg>
                {t.wa}
              </a>
            </div>
          </form>
        </FadeUp>
      </div>
    </section>
  );
};
